import { __rest } from "tslib";
import { __extends } from 'tslib';
import { assign, defaults, getBaseClass, hashMap, transform } from './tools.js';
export class Mixable {
    static define(protoProps = {}, staticProps) {
        const BaseClass = getBaseClass(this);
        staticProps && assign(this, staticProps);
        const { mixins } = protoProps, defineMixin = __rest(protoProps, ["mixins"]);
        mixins && this.mixins.merge(mixins);
        this.mixins.mergeObject(this.prototype, defineMixin, true);
        this.mixins.mergeObject(this.prototype, this.mixins.getStaticDefinitions(BaseClass), true);
        this.onDefine && this.onDefine(this.mixins.definitions, BaseClass);
        this.mixins.mergeInheritedMembers(BaseClass);
        return this;
    }
    static extend(spec, statics) {
        let TheSubclass;
        if (spec && spec.hasOwnProperty('constructor')) {
            TheSubclass = spec.constructor;
            __extends(TheSubclass, this);
        }
        else {
            TheSubclass = class Subclass extends this {
            };
        }
        predefine(TheSubclass);
        spec && TheSubclass.define(spec, statics);
        return TheSubclass;
    }
}
export function predefine(Constructor) {
    const BaseClass = getBaseClass(Constructor);
    Constructor.__super__ = BaseClass.prototype;
    Constructor.define || MixinsState.get(Mixable).populate(Constructor);
    MixinsState.get(Constructor);
    Constructor.onExtend && Constructor.onExtend(BaseClass);
}
export function define(ClassOrDefinition) {
    if (typeof ClassOrDefinition === 'function') {
        predefine(ClassOrDefinition);
        ClassOrDefinition.define();
    }
    else {
        return function (Ctor) {
            predefine(Ctor);
            Ctor.define(ClassOrDefinition);
        };
    }
}
export function definitions(rules) {
    return (Class) => {
        const mixins = MixinsState.get(Class);
        mixins.definitionRules = defaults(hashMap(), rules, mixins.definitionRules);
    };
}
export function propertyListDecorator(listName) {
    return function propList(proto, name) {
        const list = proto.hasOwnProperty(listName) ?
            proto[listName] : (proto[listName] = (proto[listName] || []).slice());
        list.push(name);
    };
}
export function definitionDecorator(definitionKey, value) {
    return (proto, name) => {
        MixinsState
            .get(proto.constructor)
            .mergeObject(proto, {
            [definitionKey]: {
                [name]: value
            }
        });
    };
}
export class MixinsState {
    static get(Class) {
        const { mixins } = Class;
        return mixins && Class === mixins.Class ? mixins :
            Class.mixins = new MixinsState(Class);
    }
    constructor(Class) {
        this.Class = Class;
        this.definitions = {};
        const { mixins } = getBaseClass(Class);
        this.mergeRules = (mixins && mixins.mergeRules) || hashMap();
        this.definitionRules = (mixins && mixins.definitionRules) || hashMap();
        this.appliedMixins = (mixins && mixins.appliedMixins) || [];
    }
    getStaticDefinitions(BaseClass) {
        const definitions = hashMap(), { Class } = this;
        return transform(definitions, this.definitionRules, (rule, name) => {
            if (BaseClass[name] !== Class[name]) {
                return Class[name];
            }
        });
    }
    merge(mixins) {
        const proto = this.Class.prototype, { mergeRules } = this;
        const appliedMixins = this.appliedMixins = this.appliedMixins.slice();
        for (let mixin of mixins) {
            if (Array.isArray(mixin)) {
                this.merge(mixin);
            }
            else if (appliedMixins.indexOf(mixin) < 0) {
                appliedMixins.push(mixin);
                if (typeof mixin === 'function') {
                    this.mergeObject(this.Class, mixin);
                    const sourceMixins = mixin.mixins;
                    if (sourceMixins) {
                        this.mergeRules = defaults(hashMap(), this.mergeRules, sourceMixins.mergeRules);
                        this.definitionRules = defaults(hashMap(), this.definitionRules, sourceMixins.definitionRules);
                        this.appliedMixins = this.appliedMixins.concat(sourceMixins.appliedMixins);
                    }
                    this.mergeObject(proto, mixin.prototype);
                }
                else {
                    this.mergeObject(proto, mixin);
                }
            }
        }
    }
    populate(...ctors) {
        for (let Ctor of ctors) {
            MixinsState.get(Ctor).merge([this.Class]);
        }
    }
    mergeObject(dest, source, unshift) {
        forEachOwnProp(source, name => {
            const sourceProp = Object.getOwnPropertyDescriptor(source, name);
            let rule;
            if (rule = this.definitionRules[name]) {
                assignProperty(this.definitions, name, sourceProp, rule, unshift);
            }
            if (!rule || rule === mixinRules.protoValue) {
                assignProperty(dest, name, sourceProp, this.mergeRules[name], unshift);
            }
        });
    }
    mergeInheritedMembers(BaseClass) {
        const { mergeRules, Class } = this;
        if (mergeRules) {
            const proto = Class.prototype, baseProto = BaseClass.prototype;
            for (let name in mergeRules) {
                const rule = mergeRules[name];
                if (proto.hasOwnProperty(name) && name in baseProto) {
                    proto[name] = resolveRule(proto[name], baseProto[name], rule);
                }
            }
        }
    }
}
const dontMix = {
    function: hashMap({
        length: true,
        prototype: true,
        caller: true,
        arguments: true,
        name: true,
        __super__: true
    }),
    object: hashMap({
        constructor: true
    })
};
function forEachOwnProp(object, fun) {
    const ignore = dontMix[typeof object];
    for (let name of Object.getOwnPropertyNames(object)) {
        ignore[name] || fun(name);
    }
}
export const mixins = (...list) => ((Class) => MixinsState.get(Class).merge(list));
export const mixinRules = ((rules) => ((Class) => {
    const mixins = MixinsState.get(Class);
    mixins.mergeRules = defaults(rules, mixins.mergeRules);
}));
mixinRules.value = (a, b) => a;
mixinRules.protoValue = (a, b) => a;
mixinRules.merge = (a, b) => defaults({}, a, b);
mixinRules.pipe = (a, b) => (function (x) {
    return a.call(this, b.call(this, x));
});
mixinRules.defaults = (a, b) => (function () {
    return defaults(a.apply(this, arguments), b.apply(this, arguments));
});
mixinRules.classFirst = (a, b) => (function () {
    a.apply(this, arguments);
    b.apply(this, arguments);
});
mixinRules.classLast = (a, b) => (function () {
    b.apply(this, arguments);
    a.apply(this, arguments);
});
mixinRules.every = (a, b) => (function () {
    return a.apply(this, arguments) && b.apply(this, arguments);
});
mixinRules.some = (a, b) => (function () {
    return a.apply(this, arguments) || b.apply(this, arguments);
});
function assignProperty(dest, name, sourceProp, rule, unshift) {
    if (dest.hasOwnProperty(name)) {
        const destProp = Object.getOwnPropertyDescriptor(dest, name);
        if (destProp.configurable && 'value' in destProp) {
            dest[name] = unshift ?
                resolveRule(sourceProp.value, destProp.value, rule) :
                resolveRule(destProp.value, sourceProp.value, rule);
        }
    }
    else {
        Object.defineProperty(dest, name, sourceProp);
    }
}
function resolveRule(dest, source, rule) {
    if (dest === void 0)
        return source;
    if (!rule || source === void 0)
        return dest;
    return rule(dest, source);
}
//# sourceMappingURL=mixins.js.map